import 'src/hooks/howlerConfig'
import { useEffect, useState } from 'react'
import { Howl } from 'howler'
import { ASSETS } from 'src/config'
import { useGetMasterVolume, useGetMusicVolume } from 'src/store/sound'
import { toSafeVolume } from 'src/utils/volumeUtils'

interface Playlist {
  songs: (string | string[])[]
}

const playlistCache = new Map<string, Howl[]>()

export const useGameMusic = (gameType: string): Howl[] => {
  const [playlist, setPlaylist] = useState<Howl[]>([])
  const masterVolume = useGetMasterVolume()
  const musicVolume = useGetMusicVolume()
  const volume = toSafeVolume(musicVolume * masterVolume, 0)

  useEffect(() => {
    let isMounted = true

    const loadPlaylist = async (type: string): Promise<Howl[]> => {
      const resolvedType = type || 'marbles'

      for (const [key, howls] of playlistCache.entries()) {
        if (key !== resolvedType) {
          howls.forEach((howl) => {
            try {
              if (howl.playing()) howl.stop()
              howl.unload()
            } catch (e) {
              console.warn(`Error unloading Howl for ${key}:`, e)
            }
          })
          playlistCache.delete(key)
        }
      }

      try {
        const res = await fetch(
          `/assets/sounds/playlists/${resolvedType}.json?${ASSETS.SOUNDS_CACHE_BUSTER}`
        )
        if (!res.ok) throw new Error(`Playlist fetch failed: ${resolvedType}`)

        const data: Playlist = await res.json()

        const howls = data.songs.map((entry) => {
          const srcList = Array.isArray(entry) ? entry : [entry]
          const sound = new Howl({
            src: srcList.map(
              (file) => `/assets/sounds/songs/processed/${file}?${ASSETS.SOUNDS_CACHE_BUSTER}`
            ),
            preload: false,
            volume,
            autoplay: false,
            html5: true,
            onend: () => {
              sound.unload()
            }
          })

          return sound
        })

        playlistCache.set(resolvedType, howls)
        return howls
      } catch (err) {
        if (resolvedType !== 'marbles') {
          return loadPlaylist('marbles')
        }
        return []
      }
    }

    void loadPlaylist(gameType).then((howls) => {
      if (isMounted) {
        setPlaylist(howls)
      } else {
        howls.forEach((howl) => {
          try {
            if (howl.playing()) howl.stop()
            howl.unload()
          } catch (e) {
            console.warn('Cleanup unload failed:', e)
          }
        })
      }
    })

    return () => {
      isMounted = false
    }
  }, [gameType])

  return playlist
}
